import { useEffect, useState } from 'react';
import { useAuth } from '@/features/auth/AuthContext';
import { hasValidPaymentMethod } from '../utils/paymentMethods';
import { subscribeToBillingProfile } from './useBillingProfile';

/**
 * ``'pro'`` when a billing profile has a valid (non-expired) card, ``'free'``
 * for a loaded profile without one, ``null`` when there is no profile.
 */
export const planTierFromProfile = (profile) => {
    if (!profile || typeof profile !== 'object') return null;
    return hasValidPaymentMethod(profile.payment_methods) ? 'pro' : 'free';
};

const normalizePlan = (plan) => (plan === 'free' || plan === 'pro' ? plan : null);

/**
 * The "Free" / "Pro" tier shown next to the user's name in the avatar menu.
 *
 * The baseline is ``plan`` from ``/users/bootstrap`` (``null`` when billing
 * is off, so the tag is hidden).  Admins — the only role allowed to open
 * Plans & Billing — additionally follow profiles published through
 * ``useBillingProfile``: attaching a card on that tab flips the tag to "Pro"
 * immediately, without another request or a reload.  Nothing is fetched
 * here; the bootstrap value already covers the first paint.
 */
export function usePlanTier() {
    const { plan, isSaas, billingEnabled, isAuthEnabled, role } = useAuth();
    const isAdmin = !isAuthEnabled || role === 'Admin';
    const followLive = isSaas === true && billingEnabled === true && isAdmin;
    const [liveProfile, setLiveProfile] = useState(null);

    useEffect(() => {
        if (!followLive) {
            setLiveProfile(null);
            return undefined;
        }
        return subscribeToBillingProfile(setLiveProfile);
    }, [followLive]);

    const liveTier = followLive ? planTierFromProfile(liveProfile) : null;
    return liveTier ?? normalizePlan(plan);
}

export default usePlanTier;
